import {loadDataXML} from 'components/customer/Preview3D/helpers';
import {Batch, PreviewFormValues} from 'components/customer/Preview3D/types';
import {TemplateVariable} from 'components/customer/Preview3D/usePreview3DData';
import {
    RoomPlannerFormValues,
    CabinetMesh,
    RoomPlannerProductWithTemplate,
} from 'components/customer/RoomPlanner/types';
import {json2xml, xml2json} from 'xml-js';
import {createSceneXML} from 'components/customer/RoomPlanner/helpers/scene';
import roomPlannerData from 'components/customer/RoomPlanner/values/useRoomPlannerData';
import {
    preloadCabinetTextures,
    getCarcaseThickness,
} from 'components/customer/Preview3DCommon/shapes/advancedShelf';
import {hasPairDoors} from 'components/customer/Preview3DCommon/helpers/shakerDoorHelper';
import {addShakerDoorsToRoomPlannerCabinet} from 'components/customer/Preview3DCommon/hardware/useShakerDoor';
import {
    normalizeLShapeCabinet,
    correctBaseCornerDoorMargins,
} from 'components/customer/Preview3DCommon/values/door';
import {hasLShapeFootprint} from 'components/customer/Preview3DCommon/helpers/hasLShapeFootprint';
import {getShakerDoorInfo} from 'components/customer/RoomPlanner/helpers/renderer/cabinetMaterialResolver';
import {applyCabinetCustomizations} from 'components/customer/RoomPlanner/helpers/renderer/cabinetCustomizer';
import {mergeCabinetValues} from 'components/customer/RoomPlanner/helpers/renderer/cabinetValuesMerger';
import {assignCabinetGroupUserData} from 'components/customer/RoomPlanner/helpers/renderer/cabinetDepthCalculator';
import {configureOpeningHeights} from 'components/customer/RoomPlanner/helpers/renderer/cabinetOpeningHeights';
import {buildLShapeInputs} from 'components/customer/RoomPlanner/helpers/renderer/lShapeInputsBuilder';
import {applyShadows} from 'components/customer/RoomPlanner/helpers/shadows';
import {
    applyDoorFrontsMode,
    tagDoorOrDrawerFront,
} from 'components/customer/RoomPlanner/helpers/doorFronts';
import productEvents from 'components/customer/RoomPlanner/lib/useProductEvents';
import {store} from 'store/customer';

// Re-export for backward compatibility
export {
    isAppliedPanelWithDepth,
    isAppliedPanelWithWidth,
    isAppliedPanelWithLength,
    isManualPanel,
    productThatDoesNotNeedAdjustableLegs,
} from 'components/customer/RoomPlanner/helpers/renderer/panelTypeHelpers';

export const generateCabinetMesh = (
    valuesParams: RoomPlannerProductWithTemplate,
    isWireframeMode = false
) => {
    let values = {...valuesParams};
    const variables = JSON.parse(
        values.template_3d[0].attributes.variables
    ) as TemplateVariable;
    const carcThickness = values.cabinet_carc_thickness ?? 0;

    const isLShapedFootprint = hasLShapeFootprint(variables);
    const templateName = values.template_3d[0].attributes.template;

    // Merge all cabinet values (L-shaped, key shape, etc.)
    values = mergeCabinetValues(
        values,
        variables,
        isLShapedFootprint,
        templateName
    );

    const json = xml2json(values.template_3d[1], {
        compact: false,
    });

    const isRobeProduct = /Robe.*\.xml$/i.test(templateName);
    const noBottom =
        variables.noCornerBaseBottom ??
        Boolean(
            variables?.fields.find((field) => field.name === 'noBottom')
                ?.expression
        );

    const isFloorProduct = templateName?.includes('KickerFramed');

    // Configure opening heights for oven, microwave, and rangehood
    configureOpeningHeights(values, variables, templateName);

    const parsedJson = roomPlannerData.replaceDimensions(
        json,
        values as unknown as RoomPlannerFormValues,
        variables,
        isRobeProduct
    );

    const data = loadDataXML(json2xml(parsedJson, {compact: false})) as Batch;

    // Build L-shape inputs for normalization and door margin corrections
    const lShapeInputs = buildLShapeInputs(values, variables, carcThickness);

    normalizeLShapeCabinet(data, templateName, variables, lShapeInputs);
    correctBaseCornerDoorMargins(data, lShapeInputs);

    const opGroups =
        data?.Jobs['IDJ-1']?.Manufacturing['IDMFG-1']?.OperationGroups;
    const isPairDoor = opGroups
        ? opGroups['IDOP-DoorRightWithReturn']?.IsPairDoor
        : undefined;
    const hasPairOfDoorsWithInnerDrawer =
        hasPairDoors(data) && (variables?.innerDrawerCount ?? 0) > 0;

    const {shakerDoorInfo, material, showTexture, door} =
        getShakerDoorInfo(values);

    // Detect kicker frame / bulkhead products so their panels get beveled edges.
    const isKickerOrBulkhead =
        Boolean(
            (
                JSON.parse(
                    values.template_3d?.[0]?.attributes?.variables ?? '{}'
                ) as {isBulkheadComponent?: boolean}
            )?.isBulkheadComponent
        ) ||
        templateName?.includes('Bulkhead') ||
        templateName?.includes('KickerFramed');

    // Start texture downloads before building template panels and advanced
    // shelves so createPanelGeometry and advancedShelf share the same cache.
    preloadCabinetTextures(values as unknown as PreviewFormValues);

    const cabinetGroup = createSceneXML(
        data,
        values,
        variables,
        true,
        values.include_drawer_faces,
        false,
        isWireframeMode,
        {
            // Carcase panels only. scene.ts swaps in simple_shelves_thickness
            // for the shelf segments themselves.
            exteriorThickness: values.cabinet_ext_thickness ?? 0,
            carcaseThickness: getCarcaseThickness(values as PreviewFormValues),
        },
        shakerDoorInfo,
        isKickerOrBulkhead
    );

    // If cabinet group creation failed, return early
    if (!cabinetGroup) {
        throw new Error('Failed to create cabinet group');
    }

    // Assign all cabinet group user data (name, basic properties, and depth)
    assignCabinetGroupUserData({
        cabinetGroup,
        values,
        variables,
        templateName,
        carcThickness,
        isLShapedFootprint,
        isFloorProduct,
        hasPairOfDoorsWithInnerDrawer,
        isRobeProduct,
        noBottom,
        isPairDoor,
    });

    // Apply adjustable legs and custom shelves
    applyCabinetCustomizations({
        cabinetGroup,
        values,
        variables,
        isLShapedFootprint,
        isRobeProduct,
        noBottom,
        templateName,
    });

    // Add shaker doors only when we actually have a shaker door (vinyl).
    // When explicit shakerDoorInfo.isShakerDoor is false (e.g. from undo backup), skip entirely
    // so we don't add any doors on top of the template's flat doors (avoids duplication).
    if (shakerDoorInfo.isShakerDoor) {
        void addShakerDoorsToRoomPlannerCabinet(
            values,
            variables,
            data, // batch data
            material,
            showTexture,
            door
        ).then((shakerDoors) => {
            if (shakerDoors?.length > 0) {
                shakerDoors.forEach((shakerDoor) => {
                    tagDoorOrDrawerFront(shakerDoor);
                    applyShadows(shakerDoor);
                });
                cabinetGroup.add(...shakerDoors);
                const mode = store.getState().roomPlanner.doorFrontsMode;
                void applyDoorFrontsMode(
                    cabinetGroup,
                    mode,
                    (group, eventName, params) =>
                        productEvents.triggerEventsAsync(
                            group,
                            eventName,
                            params
                        ),
                    {
                        animate: mode === 'open',
                        animationSpeed: 0,
                    }
                );
            }
        });
    }

    return cabinetGroup as CabinetMesh;
};
